import {
  formaterJour,
  formaterMontant,
  referentiel as r,
  regrouperAbonnements,
  type Totaux,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type Abonnement, api, corps, lire, requetes } from '../api'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { LogoConnu, LogoDe } from '../composants/Logo'
import { Ligne, Section, Vide } from '../composants/Section'
import { EntetePage, Filtre } from '../composants/Ui'
import { LIBELLES_FREQUENCE, options } from '../libelles'

// Abonnements : tout ce qui est prélevé tant qu'on ne résilie pas, par entité puis par banque,
// avec le coût par échéance, la fréquence et le coût annuel. La banque est celle du compte qui
// paie : un abonnement réglé par la carte d'une entité est rangé chez cette entité.

const montant = (cts: number | null, devise: string) =>
  cts === null ? 'Inconnu' : formaterMontant(cts, devise)

function total(totaux: Totaux) {
  const lignes = Object.entries(totaux)
  return lignes.length
    ? lignes.map(([devise, cts]) => formaterMontant(cts, devise)).join(' + ')
    : formaterMontant(0)
}

function champsAbonnement(
  tiers: { id: string; nom: string }[],
  comptes: { id: string; libelle: string; banque: string }[],
): Champ[] {
  return [
    { nom: 'libelle', libelle: 'Abonnement', type: 'texte', requis: true },
    {
      nom: 'tiersId',
      libelle: 'Fournisseur',
      type: 'choix',
      requis: true,
      cherchable: true,
      options: tiers.map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
    {
      nom: 'frequence',
      libelle: 'Fréquence',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_FREQUENCE),
    },
    { nom: 'montantCts', libelle: 'Coût par échéance (€)', type: 'montant' },
    {
      nom: 'compteId',
      libelle: 'Compte qui paie',
      type: 'choix',
      options: comptes.map((c) => ({ valeur: c.id, libelle: `${c.banque} · ${c.libelle}` })),
    },
    {
      nom: 'motifReleve',
      libelle: 'Mots du relevé',
      type: 'texte',
      aide: 'Partie du libellé bancaire qui désigne ce prélèvement, pour suivre le dernier débit.',
    },
  ]
}

function DernierDebit({ a }: { a: Abonnement }) {
  if (!a.dernierDebit) {
    return a.etat === 'sans_debit_recent' ? (
      <span className="alerte">Aucun débit trouvé</span>
    ) : (
      <span className="discret">Non suivi</span>
    )
  }
  const debit = `${formaterJour(a.dernierDebit.date)} · ${formaterMontant(a.dernierDebit.montantCts, a.devise)}`
  return a.etat === 'sans_debit_recent' ? (
    <span className="alerte">Plus rien depuis : {debit}</span>
  ) : (
    <span className="discret">{debit}</span>
  )
}

function SectionEntite({
  entiteId,
  entite,
  abonnements,
}: {
  entiteId: string
  entite: string
  abonnements: Abonnement[]
}) {
  const client = useQueryClient()
  const ecriture = useQuery(requetes.entite(entiteId)).data?.droits.ecriture ?? false
  const tiers = useQuery(requetes.tiers).data ?? []
  const comptes = useQuery(requetes.comptes(entiteId)).data ?? []
  const champs = champsAbonnement(tiers, comptes)
  const { entites } = regrouperAbonnements(abonnements)
  const banques = entites[0]?.banques ?? []
  const rafraichir = () => client.invalidateQueries({ queryKey: ['abonnements'] })

  return (
    <Section
      titre={`${entite} · ${total(entites[0]?.totaux ?? {})} par an`}
      ajout={
        ecriture
          ? (fermer) => (
              <Formulaire
                champs={champs}
                onAnnuler={fermer}
                onValider={async (v) => {
                  await lire(
                    api.contrats.$post({
                      json: corps(r.contratCreation, { ...v, entiteId, type: 'abonnement' }),
                    }),
                  )
                  await rafraichir()
                  fermer()
                }}
              />
            )
          : undefined
      }
    >
      {banques.length === 0 && <Vide>Aucun abonnement.</Vide>}
      {banques.map((b) => (
        <div key={b.banque ?? 'inconnue'} className="abonnements-banque">
          <div className="abonnements-banque-titre">
            <h3 className="titre-logo">
              <LogoConnu nom={b.banque} taille={24} />
              {b.banque ?? 'Banque non identifiée'}
            </h3>
            <span>{total(b.totaux)} par an</span>
          </div>
          <div className="abonnement-grille abonnement-entete discret">
            <span>Abonnement</span>
            <span>Coût</span>
            <span>Fréquence</span>
            <span>Coût annuel</span>
            <span>Dernier débit</span>
          </div>
          {b.lignes.map((a) => (
            <Ligne
              key={a.id}
              edition={
                ecriture
                  ? (fermer) => (
                      <Formulaire
                        champs={champs}
                        initial={a}
                        onAnnuler={fermer}
                        onValider={async (v) => {
                          await lire(
                            api.contrats[':id'].$patch({
                              param: { id: a.id },
                              json: corps(r.contratModification, v),
                            }),
                          )
                          await rafraichir()
                          fermer()
                        }}
                      />
                    )
                  : undefined
              }
              onSupprimer={
                ecriture
                  ? async () => {
                      await lire(api.contrats[':id'].$delete({ param: { id: a.id } }))
                      await rafraichir()
                    }
                  : undefined
              }
            >
              <div className="abonnement-grille">
                <span className="titre-logo">
                  <LogoDe nom={a.tiers} />
                  <span>
                    <strong>{a.libelle}</strong>
                    <span className="discret abonnement-tiers">{a.tiers}</span>
                  </span>
                </span>
                <span data-libelle="Coût">{montant(a.montantCts, a.devise)}</span>
                <span data-libelle="Fréquence">{LIBELLES_FREQUENCE[a.frequence]}</span>
                <strong data-libelle="Coût annuel">{montant(a.coutAnnuelCts, a.devise)}</strong>
                <span data-libelle="Dernier débit">
                  <DernierDebit a={a} />
                </span>
              </div>
            </Ligne>
          ))}
        </div>
      ))}
    </Section>
  )
}

export function Abonnements({ entiteId }: { entiteId?: string | undefined }) {
  const navigate = useNavigate()
  const entites = useQuery(requetes.entites).data ?? []
  const abonnements = useQuery(requetes.abonnements(entiteId))
  const lignes = abonnements.data ?? []
  const actives = entites.filter((e) => !e.archivee && (!entiteId || e.id === entiteId))
  return (
    <main>
      <EntetePage surtitre="Paiements" titre="Abonnements" />
      <div className="pile" style={{ gap: 16 }}>
        <p className="discret">
          Ce qui est prélevé tant qu’on ne résilie pas, par entité puis par banque. Le coût annuel
          multiplie le coût par la fréquence ; pour un prix qui varie (consommation, dollar), le
          coût est une moyenne des derniers débits.
        </p>
        <div className="rangee-filtres" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Filtre
            icone="domain"
            libelle="Entité"
            valeur={entiteId ?? ''}
            onChange={(v) => navigate({ to: '/abonnements', search: v ? { entiteId: v } : {} })}
            options={[
              { valeur: '', libelle: 'Toutes les entités' },
              ...entites.filter((e) => !e.archivee).map((e) => ({ valeur: e.id, libelle: e.nom })),
            ]}
          />
        </div>
        {abonnements.data && (
          <p className="abonnements-total">
            Total : <strong>{total(regrouperAbonnements(lignes).totaux)}</strong> par an
          </p>
        )}
        {actives.map((e) => (
          <SectionEntite
            key={e.id}
            entiteId={e.id}
            entite={e.nom}
            abonnements={lignes.filter((a) => a.entiteId === e.id)}
          />
        ))}
      </div>
    </main>
  )
}
